import UserEvent from "../../core/UserEvent";
import Box from "../Box";
import {Checkbox, FormControlLabel, Tooltip, Typography, useTheme} from "@material-ui/core";
import i18n from "../../core/i18n";
import React from "react";
import UniversalField from "../UniversalField";
import PropTypes from "prop-types";
import {Questionnaire} from "../../qnr";
import lang from "../../core/lang";
import Button from "../Button";
import VisibilitySensor from "react-visibility-sensor";
import {Skeleton} from "@material-ui/lab";
import CircleIndicator from "./CircleIndicator";
import infoIcon from "@iconify/icons-ic/info";
import roundRestartAlt from "@iconify/icons-ic/round-restart-alt";
import { Icon } from "@iconify/react";

function defaultChangeIndicatorRenderer(sequence) {
    return <CircleIndicator sequence={sequence} />
}

function defaultTailIndicatorRenderer() {
    return null;
}

function YNQuestion(props) {
    const {value, change, index, validation, disabledColor, diff, showUndo} = props;
    const question = change || value;
    const answer = question.answer;
    const {description, helperText, required} = question;
    const disabled = (question.disabled || props.disabled);
    const readOnly = props.readOnly;
    const opacity = (props.readOnly && !question.disabled) ? 1 : undefined;
    const theme = useTheme();
    const [isVisible, setIsVisible] = React.useState(false);

    const handleChange = event => {
        const {currentTarget} = event;
        const {value, checked} = currentTarget;
        const newAnswer = checked ? (value==='true') : null;
        const newQuestion = question.clone();
        newQuestion.answer = newAnswer;
        props.onChange && props.onChange(new UserEvent({value: newQuestion}))
    }

    const handleElaborationChange = event => {
        if(event.bounce) return;
        const {value} = event;
        if(question.answer) {
            const newQuestion = question.clone();
            newQuestion.answer.elaboration = value;
            props.onChange && props.onChange(new UserEvent({value: newQuestion}))
        }
    }

    const handleUndo = () => {
        props.onChange && props.onChange(new UserEvent({value}))
    }

    let elaboration;
    const options = question.options.map((option, index) => {
        const checked = (option.value===answer?.value);
        const value = option.value;
        const text = option.text || i18n.translate(option.value ? 'Yes' : 'No');
        checked && option.elaboration && (elaboration = option.elaboration);
        const dependencies = question.questions.reduce((dependencies, question, index) => {
            if(question.test(option.value)) {
                dependencies.push(props.index + 1 + index + 1);
            }
            return dependencies;
        }, []);
        const info = <Typography>{i18n.translate('Selecting this option requires answering questions %s below.', dependencies)}</Typography>;
        const label = (
          <Box display="flex" alignItems="center">
            <Typography>{text}</Typography>
            {dependencies.length > 0 ? (
              <Tooltip title={info}>
                <Icon
                  fontSize="1.2rem"
                  color={theme.palette.secondary.main}
                  icon={infoIcon}
                />
              </Tooltip>
            ) : null}
          </Box>
        );
        return (
            <Box key={index} display="inline" disabled={disabled || readOnly} opacity={opacity}>
                <FormControlLabel value={value} checked={checked} control={<Checkbox />} label={label} onChange={handleChange} />
            </Box>
        )
    })

    let elaborationField;
    if(elaboration) {
        const value = question?.answer?.elaboration || '';
        const {required, type, description, multiple, min, max, step, minlength, maxlength, pattern, placeholder, helperText} = elaboration;
        const fullWidth = (type==='text' || type==='string' || type==='file' || multiple);
        const size = multiple ? 'medium' : 'small';
        elaborationField = <UniversalField value={value} type={type==='string' ? 'text' : type} disabled={disabled} readOnly={readOnly} debounce
                                           multiple={multiple} min={min} max={max} step={step} minlength={minlength} maxlength={maxlength} pattern={pattern}
                                           label={description} required={required} onChange={handleElaborationChange}
                                           placeholder={placeholder} helperText={helperText}
                                           variant="outlined" size={size} fullWidth={fullWidth}/>
    }

    const indicator = (required && !question.disabled) ? '*' : '';
    const helperTextNode = disabled ? null : <Typography variant="caption" display="block">{helperText}</Typography>;
    const warning = validation && !disabled && !question.isCompleted();
    const color = disabled ? disabledColor : 'unset';
    const isChanged = !(lang.isNullOrUndefined(answer, value.answer) || lang.isEqual(answer, value.answer));
    const undo = (showUndo && !readOnly && !disabled && isChanged) ? <Button variant="icon" icon={<Icon icon={roundRestartAlt} />} size="small" onClick={handleUndo} /> : null;
    const changeIndicatorRenderer = props.changeIndicator || defaultChangeIndicatorRenderer;
    const changeIndicator = diff && !disabled && isChanged ? changeIndicatorRenderer(index) : null;
    const tailIndicatorRender = props.tailIndicatorRenderer || defaultTailIndicatorRenderer;
    const tailIndicator = tailIndicatorRender({isChanged, isDisabled: disabled, isReadOnly: readOnly});

    return (
        <VisibilitySensor onChange={isVisible => isVisible && setIsVisible(isVisible)}
                          partialVisibility>
            {isVisible ? <Box disabled={disabled} opacity={opacity} color={color} paddingX={1}>
                <Box display="flex">
                    <Box position="relative" display="flex">
                        <Typography color={warning ? 'error' : 'initial'}>{`${index+1}.`}</Typography>
                        {changeIndicator}
                    </Box>
                    <Typography color={warning ? 'error' : 'initial'}>{`${description}`}{indicator}</Typography>
                    {undo}
                    {tailIndicator}
                </Box>
                <Box marginY={1}>
                    {options}
                    {elaborationField}
                    {helperTextNode}
                </Box>
            </Box> : <Skeleton animation="wave" />}
        </VisibilitySensor>

    )
}

YNQuestion.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.instanceOf(Questionnaire.Question)
    ]),
    index: PropTypes.number,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    disabled: PropTypes.bool,
    // 是否檢視檢核結果
    validation: PropTypes.bool,
    disabledColor: PropTypes.string,
    // 標示變更的差異
    diff: PropTypes.bool,
    showUndo: PropTypes.bool,
    variant: PropTypes.oneOf(['legacy', 'classic']),
    changeIndicator: PropTypes.func,
    tailIndicatorRenderer: PropTypes.func,
}

YNQuestion.defaultProps = {
    variant: 'classic',
    showUndo: true
}

export default YNQuestion;